import { useCallback, useEffect, useRef, useState } from 'react';
import { WordRecognizer, prewarmSpeechModel } from './speechRecognizer';
import { matchCandidate } from './speechAnswerEvaluator';

// Single reusable hook for every "Говорить" exercise: handles mic permission,
// spins up a Kaldi recognizer scoped to the current question's candidate
// answers, and resolves once one of them is recognized or a timeout passes.
// The component stays in charge of what "correct" means — it just compares
// the returned matchedKey to whichever candidate key it considers correct.
//
// candidates: [{ key, text }] — text may be a single word or a phrase.
// exerciseLabel is optional and only used for the "[speech] exercise: ..."
// console log, so it's easy to tell which exercise a recognition log came
// from when several screens share this hook.
export function useSpeakAnswer(exerciseLabel) {
  const recognizerRef = useRef(null);
  const streamRef = useRef(null);
  const timeoutRef = useRef(null);
  // Bumped on every cleanup() — including the one at the top of every
  // start() call. start() is async and not cancelled when called again
  // (e.g. two questions in quick succession), so without this a *stale*
  // invocation resuming after an `await` (mic permission, model load) can
  // overwrite the refs a *newer* invocation already set up: it clobbers
  // timeoutRef/recognizerRef with its own stale objects, which showed up as
  // the next question's recognizer instantly "timing out" (really the
  // previous question's leftover timer firing) and silently going deaf.
  // Every async continuation below checks isCurrent() before touching
  // shared state or calling back out.
  const sessionRef = useRef(0);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Background-load the Vosk model as soon as a speak-capable screen
    // mounts, so pressing "Говорить" later doesn't pay the load cost.
    prewarmSpeechModel();
  }, []);

  useEffect(() => {
    // Ask for mic permission as soon as a speak-capable screen mounts, not
    // on the first real start() call. The first-ever getUserMedia() of a
    // session pops a permission dialog, and some browsers suppress/delay
    // other tab audio while it's open — if that collides with a question's
    // voiceover (started at the same time as recognition, by design, so the
    // recognizer is warm the instant the child can answer), the very first
    // question's audio can get eaten. Resolving permission here, decoupled
    // from any specific question, avoids that collision for every question
    // after the first.
    navigator.mediaDevices?.getUserMedia?.({ audio: true })
      .then((stream) => stream.getTracks().forEach((track) => track.stop()))
      .catch(() => {}); // denied/unavailable — the real start() call will surface this properly
  }, []);

  const cleanup = useCallback(() => {
    sessionRef.current += 1;
    clearTimeout(timeoutRef.current);
    recognizerRef.current?.stop();
    recognizerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setListening(false);
  }, []);

  const start = useCallback(
    // continuous: keep the same recognizer (same grammar) running and call
    // onResult for every match instead of tearing down after the first —
    // for a multi-step question (e.g. one word's several syllables) where
    // the candidate set doesn't change between steps, recreating the
    // recognizer per step wastes the ~300ms Kaldi grammar-estimation grace
    // period (speechRecognizer.js) for nothing, and can miss speech that
    // starts right as the old recognizer is torn down. Caller is
    // responsible for calling stop() once continuous listening should end.
    async (candidates, { onResult, onListening, onRawText, timeoutMs = 8000, continuous = false, threshold } = {}) => {
      cleanup();
      const mySession = sessionRef.current;
      const isCurrent = () => sessionRef.current === mySession;
      setError(null);
      let settled = false;
      const finish = (result) => {
        if (settled || !isCurrent()) return;
        settled = true;
        console.log('[speech] result:', result);
        cleanup();
        onResult?.(result);
      };
      const emit = (result) => {
        if (settled || !isCurrent()) return;
        console.log('[speech] result:', result);
        if (continuous) onResult?.(result);
        else finish(result);
      };

      try {
        console.log('[speech] exercise:', exerciseLabel || '(unlabeled)', 'candidates:', candidates);
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!isCurrent()) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;

        const recognizer = new WordRecognizer(candidates.map((c) => c.text));
        if (!isCurrent()) {
          recognizer.stop();
          return;
        }
        recognizerRef.current = recognizer;

        timeoutRef.current = setTimeout(() => {
          if (!isCurrent()) return;
          console.log('[speech] timed out waiting for a match');
          finish({ matchedKey: null, said: '', timedOut: true });
        }, timeoutMs);

        await recognizer.start(stream, (text) => {
          if (!isCurrent()) return;
          // Vosk emits one 'result' per pause-delimited chunk, not one per
          // utterance — a word said, then a pause, then another word arrive
          // as two separate chunks. onRawText hands the caller every chunk
          // as it comes in (regardless of whether it alone matches a
          // candidate) so a multi-word answer spoken with pauses between
          // words can be accumulated and matched across chunks.
          onRawText?.(text);
          const match = matchCandidate(text, candidates, threshold !== undefined ? { threshold } : undefined);
          console.log('[speech] heard:', text, '-> match:', match);
          if (match) emit({ matchedKey: match.key, said: text, timedOut: false });
        });

        if (!isCurrent()) return;

        // Only fires once the mic is actually connected to a ready
        // recognizer (see the delay in speechRecognizer.js) — safe for the
        // UI to use as "the app is really listening now" instead of firing
        // as soon as the previous TTS announcement ends.
        if (!settled) {
          setListening(true);
          onListening?.();
        }
      } catch (e) {
        if (!isCurrent()) return;
        console.error('[speech] error:', e);
        setError(e);
        finish({ matchedKey: null, said: '', timedOut: false, error: e });
      }
    },
    [cleanup, exerciseLabel]
  );

  useEffect(() => cleanup, [cleanup]);

  return { start, stop: cleanup, listening, error };
}
